import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
  Button,
  Divider,
  Chip,
  Alert,
  Snackbar
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Notifications as NotificationsIcon,
  Palette as PaletteIcon,
  Security as SecurityIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Message as MessageIcon,
  Brightness4 as DarkModeIcon,
  Brightness7 as LightModeIcon,
  Language as LanguageIcon,
  Compress as CompressIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useUserSettings } from '../contexts/UserSettingsContext';

const AccountSettingsPage: React.FC = () => {
  const { currentUser } = useAuth();
  const { settings, updateSetting, resetSettings, saveSettings } = useUserSettings();

  // حالة رسائل التأكيد
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'warning' | 'info'
  });

  // تحديث إعداد معين
  const handleSettingChange = (key: keyof typeof settings, value: any) => {
    updateSetting(key, value);
  };

  // حفظ الإعدادات
  const handleSaveSettings = () => {
    try {
      saveSettings();
      setSnackbar({
        open: true,
        message: 'تم حفظ الإعدادات بنجاح',
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'فشل في حفظ الإعدادات',
        severity: 'error'
      });
    }
  };

  // إعادة تعيين الإعدادات
  const handleResetSettings = () => {
    resetSettings();
    setSnackbar({
      open: true,
      message: 'تم إعادة تعيين الإعدادات إلى القيم الافتراضية',
      severity: 'info'
    });
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* رأس الصفحة */}
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Box display="flex" alignItems="center" gap={2}>
          <SettingsIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
              إعدادات الحساب
            </Typography>
            <Typography variant="body1" color="text.secondary">
              قم بتخصيص إعدادات حسابك وتفضيلاتك الشخصية
            </Typography>
          </Box>
        </Box>
      </Paper>

      <Grid container spacing={3}>
        {/* إعدادات الإشعارات */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
                <NotificationsIcon color="primary" />
                الإشعارات
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box display="flex" flexDirection="column" gap={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.emailNotifications}
                      onChange={(e) => handleSettingChange('emailNotifications', e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1">إشعارات البريد الإلكتروني</Typography>
                      <Typography variant="body2" color="text.secondary">
                        تلقي إشعارات عبر البريد الإلكتروني
                      </Typography>
                    </Box>
                  }
                />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.pushNotifications}
                      onChange={(e) => handleSettingChange('pushNotifications', e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1">الإشعارات الفورية</Typography>
                      <Typography variant="body2" color="text.secondary">
                        تلقي إشعارات فورية في المتصفح
                      </Typography>
                    </Box>
                  }
                />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.maintenanceNotifications}
                      onChange={(e) => handleSettingChange('maintenanceNotifications', e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1">إشعارات الصيانة</Typography>
                      <Typography variant="body2" color="text.secondary">
                        تلقي إشعارات حول عمليات الصيانة
                      </Typography>
                    </Box>
                  }
                />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.salesNotifications}
                      onChange={(e) => handleSettingChange('salesNotifications', e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1">إشعارات المبيعات</Typography>
                      <Typography variant="body2" color="text.secondary">
                        تلقي إشعارات حول المبيعات
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* إعدادات المظهر والعرض */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
                <PaletteIcon color="primary" />
                المظهر والعرض
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box display="flex" flexDirection="column" gap={3}>
                <FormControl fullWidth>
                  <InputLabel>المظهر</InputLabel>
                  <Select
                    value={settings.theme}
                    label="المظهر"
                    onChange={(e) => handleSettingChange('theme', e.target.value)}
                    startAdornment={
                      settings.theme === 'light' ? 
                        <LightModeIcon sx={{ mr: 1, color: 'text.secondary' }} /> :
                        <DarkModeIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    }
                  >
                    <MenuItem value="light">
                      <Box display="flex" alignItems="center" gap={1}>
                        <LightModeIcon fontSize="small" />
                        فاتح
                      </Box>
                    </MenuItem>
                    <MenuItem value="dark">
                      <Box display="flex" alignItems="center" gap={1}>
                        <DarkModeIcon fontSize="small" />
                        داكن
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
                
                <FormControl fullWidth>
                  <InputLabel>اللغة</InputLabel>
                  <Select
                    value={settings.language}
                    label="اللغة"
                    onChange={(e) => handleSettingChange('language', e.target.value)}
                    startAdornment={<LanguageIcon sx={{ mr: 1, color: 'text.secondary' }} />}
                  >
                    <MenuItem value="ar">العربية</MenuItem>
                    <MenuItem value="en">English</MenuItem>
                  </Select>
                </FormControl>
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.compactMode}
                      onChange={(e) => handleSettingChange('compactMode', e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Box display="flex" alignItems="center" gap={1}>
                      <CompressIcon fontSize="small" />
                      <Typography variant="body1">الوضع المضغوط</Typography>
                    </Box>
                  }
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* إعدادات الخصوصية والأمان */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
                <SecurityIcon color="primary" />
                الخصوصية والأمان
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box display="flex" flexDirection="column" gap={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.showEmail}
                      onChange={(e) => handleSettingChange('showEmail', e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1" display="flex" alignItems="center" gap={1}>
                        <EmailIcon fontSize="small" />
                        إظهار البريد الإلكتروني
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        السماح للآخرين برؤية بريدك الإلكتروني
                      </Typography>
                    </Box>
                  }
                />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.showPhone}
                      onChange={(e) => handleSettingChange('showPhone', e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1" display="flex" alignItems="center" gap={1}>
                        <PhoneIcon fontSize="small" />
                        إظهار رقم الهاتف
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        السماح للآخرين برؤية رقم هاتفك
                      </Typography>
                    </Box>
                  }
                />
                
                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.allowMessages}
                      onChange={(e) => handleSettingChange('allowMessages', e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body1" display="flex" alignItems="center" gap={1}>
                        <MessageIcon fontSize="small" />
                        السماح بالرسائل
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        السماح للزملاء بإرسال رسائل إليك
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* معلومات الحساب */}
        <Grid item xs={12} md={6}>
          <Card elevation={2}>
            <CardContent>
              <Typography variant="h6" gutterBottom display="flex" alignItems="center" gap={1}>
                <PersonIcon color="primary" />
                معلومات الحساب
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              <Box display="flex" flexDirection="column" gap={2}>
                <TextField
                  label="الاسم"
                  value={settings.name}
                  onChange={(e) => handleSettingChange('name', e.target.value)}
                  fullWidth
                  InputProps={{
                    startAdornment: <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  }}
                />
                
                <TextField
                  label="البريد الإلكتروني"
                  value={settings.email}
                  onChange={(e) => handleSettingChange('email', e.target.value)}
                  fullWidth
                  InputProps={{
                    startAdornment: <EmailIcon sx={{ mr: 1, color: 'text.secondary' }} />
                  }}
                />
                
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    حالة الحساب
                  </Typography>
                  <Chip
                    label={settings.status === 'active' ? 'نشط' : 'غير نشط'}
                    color={settings.status === 'active' ? 'success' : 'error'}
                    icon={<CheckCircleIcon />}
                  />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* أزرار التحكم */}
      <Paper elevation={2} sx={{ p: 3, mt: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="body2" color="text.secondary">
            سيتم حفظ إعداداتك تلقائياً في متصفحك
          </Typography>
          
          <Box display="flex" gap={2}>
            <Button
              variant="outlined"
              onClick={handleResetSettings}
            >
              إعادة تعيين
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveSettings}
            >
              حفظ الإعدادات
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* رسائل التأكيد */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AccountSettingsPage;
